/**
 * Résolution clic boutons Laika — pas d'autopilote.
 * Contract aligné backend ActionButton (requires_confirmation / auto_execute).
 */

import type { ActionButton } from './types';

export type LaikaChatTranslate = (
  key: string,
  options?: Record<string, string>,
) => string;

export type LaikaActionClickResult =
  | { kind: 'consent' }
  | { kind: 'report_bug' }
  | { kind: 'navigate'; href: string; external: boolean }
  | { kind: 'confirm_navigate'; href: string; external: boolean; confirmMessage: string }
  | { kind: 'message'; text: string }
  | { kind: 'noop' };

export type OpenLaikaHrefDeps = {
  openWindow: (url: string, target?: string, features?: string) => Window | null;
  assignLocation: (url: string) => void;
  navigateInternal: (path: string) => void;
};

/** mailto/tel : location.assign (window.open no-op fréquent). http : nouvel onglet. */
export function openLaikaHref(
  href: string,
  external: boolean,
  deps: OpenLaikaHrefDeps,
): void {
  if (!external) {
    deps.navigateInternal(href);
    return;
  }
  if (/^(mailto|tel):/i.test(href)) {
    deps.assignLocation(href);
    return;
  }
  deps.openWindow(href, '_blank', 'noopener,noreferrer');
}

/** Copy compréhensible — gate UI confirm avant navigation copilote. */
export function buildLinkConfirmMessage(
  action: ActionButton,
  href: string,
  external: boolean,
  t?: LaikaChatTranslate,
): string {
  const fallbackLabel = t ? t('chatWidget.confirmActionLabel') : 'cette action';
  const label = (action.label || fallbackLabel).trim();
  if (external) {
    const head = t
      ? t('chatWidget.confirmOpenNewTab', { label })
      : `Ouvrir « ${label} » dans un nouvel onglet ?`;
    return `${head}\n\n${href}`;
  }
  const head = t
    ? t('chatWidget.confirmOpenInApp', { label })
    : `Ouvrir « ${label} » dans Rintintin ?`;
  return `${head}\n\nChemin : ${href}`;
}

/** Première ligne seulement — pas de chemin /app brut dans le chat. */
export function inlineConfirmPrompt(confirmMessage: string, fallback = 'Ouvrir dans Rintintin ?'): string {
  const first = (confirmMessage || '').split('\n')[0].trim();
  return first || fallback;
}

/** Normalise snake_case API → champs FE. */
export function normalizeActionButton(raw: ActionButton | Record<string, unknown>): ActionButton {
  const r = raw as Record<string, unknown>;
  const requires =
    typeof r.requires_confirmation === 'boolean'
      ? r.requires_confirmation
      : typeof r.requiresConfirmation === 'boolean'
        ? r.requiresConfirmation
        : undefined;
  const auto =
    typeof r.auto_execute === 'boolean'
      ? r.auto_execute
      : typeof r.autoExecute === 'boolean'
        ? r.autoExecute
        : undefined;
  return {
    ...(raw as ActionButton),
    requires_confirmation: requires,
    auto_execute: auto,
  };
}

export function resolveLaikaActionClick(
  action: ActionButton,
  t?: LaikaChatTranslate,
): LaikaActionClickResult {
  const a = normalizeActionButton(action);

  if (a.type === 'consent') {
    return { kind: 'consent' };
  }

  if (a.type === 'report_bug' || a.id === 'contactHuman') {
    return { kind: 'report_bug' };
  }

  // Jamais d'exécution automatique même si le backend envoie auto_execute
  if (a.auto_execute === true && a.type === 'link' && a.payload) {
    const href = a.payload;
    const external = !href.startsWith('/');
    return {
      kind: 'confirm_navigate',
      href,
      external,
      confirmMessage: buildLinkConfirmMessage(a, href, external, t),
    };
  }

  if (a.type === 'link' && a.payload) {
    const href = a.payload;
    const external = !href.startsWith('/');
    if (a.requires_confirmation === true) {
      return {
        kind: 'confirm_navigate',
        href,
        external,
        confirmMessage: buildLinkConfirmMessage(a, href, external, t),
      };
    }
    return { kind: 'navigate', href, external };
  }

  const msgToSend = a.payload || a.message;
  if (msgToSend) {
    return { kind: 'message', text: msgToSend };
  }
  return { kind: 'noop' };
}

/** Normalise une liste d'actions API (chemin JSON non-SSE inclus). */
export function normalizeActionButtons(
  actions: unknown[] | null | undefined,
): ActionButton[] | undefined {
  if (!actions || !Array.isArray(actions) || actions.length === 0) {
    return undefined;
  }
  return actions.map((a) => normalizeActionButton(a as ActionButton | Record<string, unknown>));
}

